import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import {
    Search, Eye, Trash3, Telephone, Envelope, BoxArrowUpRight, XLg
} from 'react-bootstrap-icons';
import Pagination from '@mui/material/Pagination';
import AdminLayout, { isStaff } from '../AdminLayout';
import ConfirmModal from '../../../common/ui/ConfirmModal';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../../common/ui/StateBlock';
import notify, { currentLanguage } from '../../../common/ui/notify';
import language from '../../../data/language';
import { display, formatDateTime, isBlank } from '../../../common/format';

const PAGE_SIZE = 15;
const SEARCH_DEBOUNCE_MS = 400;

const STATUSES = ['new', 'contacted', 'qualified', 'closed', 'rejected'];
const SOURCES = ['home_form', 'contact_form', 'car_enquiry', 'callback', 'subscribe', 'other'];

// Badge colour per pipeline stage.
const STATUS_TONE = {
    new: 'cx-badge-new',
    contacted: 'cx-badge-neutral',
    qualified: 'cx-badge-sale',
    closed: 'cx-badge-sold',
    rejected: 'cx-badge-danger'
};

const AdminLeads = () => {
    const lang = currentLanguage();
    const strings = language[lang].leads;
    const marketStrings = language[lang].market;
    const common = language[lang].common;
    const user = useSelector((state) => state.user);

    const [leads, setLeads] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [counts, setCounts] = useState({});
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState("");
    const [source, setSource] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [keywordInput, setKeywordInput] = useState("");
    const [keyword, setKeyword] = useState("");
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [reload, setReload] = useState(0);

    const [active, setActive] = useState(null);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const requestRef = useRef(0);
    const canDelete = user.role === 'admin';

    const statusLabel = (value) => strings[`status_${value}`] || value;
    const sourceLabel = (value) => strings[`source_${value}`] || value;

    useEffect(() => {
        const timer = setTimeout(() => {
            if (keywordInput !== keyword) {
                setKeyword(keywordInput);
                setPage(1);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [keywordInput, keyword]);

    const fetchLeads = useCallback((signal) => {
        const requestId = ++requestRef.current;
        setLoading(true);
        setFailed(false);

        const params = {limit: PAGE_SIZE, page};
        if (keyword) params.keyword = keyword;
        if (status) params.status = status;
        if (source) params.source = source;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;

        return axios.get('/api/leads', {params, signal})
            .then((res) => {
                if (requestId !== requestRef.current) return;
                setLeads(Array.isArray(res.data.data) ? res.data.data : []);
                setTotal(Number(res.data.total) || 0);
                setTotalPages(Number(res.data.total_pages) || 0);
                setCounts(res.data.status_counts || {});
                if (res.data.page && Number(res.data.page) !== page) setPage(Number(res.data.page));
                setLoading(false);
            })
            .catch((error) => {
                if (axios.isCancel(error) || error.name === 'CanceledError') return;
                if (requestId !== requestRef.current) return;
                setLeads([]);
                setTotal(0);
                setFailed(true);
                setLoading(false);
            });
    }, [page, keyword, status, source, dateFrom, dateTo]);

    useEffect(() => {
        if (!isStaff(user)) return undefined;
        const controller = new AbortController();
        fetchLeads(controller.signal);
        return () => controller.abort();
    }, [fetchLeads, reload, user]);

    const refresh = () => setReload((prev) => prev + 1);

    const openLead = (lead) => {
        setActive(lead);
        setNote(lead.admin_note || "");
    };

    const patchLead = async (id, payload, successMessage) => {
        setSaving(true);
        try {
            const res = await axios.put(`/api/leads/${id}`, payload);
            notify.success(successMessage);
            // Update the row in place so the table does not jump around.
            setLeads((prev) => prev.map((item) => (item.id === id ? {...item, ...res.data.data} : item)));
            setActive((prev) => (prev && prev.id === id ? {...prev, ...res.data.data} : prev));
            refresh();
        } catch (error) {
            notify.apiError(error);
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await axios.delete(`/api/leads/${deleteTarget.id}`);
            notify.success(strings.lead_deleted);
            setDeleteTarget(null);
            if (active && active.id === deleteTarget.id) setActive(null);
            refresh();
        } catch (error) {
            notify.apiError(error);
        } finally {
            setDeleting(false);
        }
    };

    const clearFilters = () => {
        setKeywordInput("");
        setKeyword("");
        setStatus("");
        setSource("");
        setDateFrom("");
        setDateTo("");
        setPage(1);
    };

    const hasFilters = Boolean(keyword || status || source || dateFrom || dateTo);

    const carLabel = (lead) => {
        const parts = [lead.car_year, lead.car_mark, lead.car_model].filter((part) => !isBlank(part));
        return parts.length > 0 ? parts.join(" ") : null;
    };

    const body = () => {
        if (loading) return <LoadingBlock label={common.loading} />;

        if (failed) {
            return (
                <ErrorBlock
                    title={common.error_title}
                    message={common.error_message}
                    retryLabel={common.retry}
                    onRetry={refresh}
                />
            );
        }

        if (leads.length === 0) {
            return (
                <EmptyBlock
                    title={strings.no_leads_title}
                    message={strings.no_leads_message}
                    action={hasFilters && (
                        <button type="button" className="cx-btn cx-btn-primary" onClick={clearFilters}>
                            {marketStrings.clear_filters}
                        </button>
                    )}
                />
            );
        }

        return (
            <>
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th scope="col">{strings.table_client}</th>
                                <th scope="col" className="admin-hide-sm">{strings.table_contact}</th>
                                <th scope="col" className="admin-hide-md">{strings.table_source}</th>
                                <th scope="col" className="admin-hide-lg">{strings.table_car}</th>
                                <th scope="col">{strings.table_status}</th>
                                <th scope="col" className="admin-hide-md">{strings.table_date}</th>
                                <th scope="col" className="admin-table-actions-col">{strings.table_actions}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map((lead) => (
                                <tr key={lead.id}>
                                    <td>
                                        <span className="admin-table-primary">
                                            {[lead.name, lead.surname].filter(Boolean).join(" ") || "—"}
                                        </span>
                                        <span className="admin-table-secondary admin-show-sm">{display(lead.phone)}</span>
                                    </td>
                                    <td className="admin-hide-sm">
                                        <div className="admin-contact-cell">
                                            {!isBlank(lead.phone) && (
                                                <a href={`tel:${String(lead.phone).replace(/\s/g, "")}`}>
                                                    <Telephone aria-hidden="true" /> {lead.phone}
                                                </a>
                                            )}
                                            {!isBlank(lead.email) && (
                                                <a href={`mailto:${lead.email}`}>
                                                    <Envelope aria-hidden="true" /> {lead.email}
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                    <td className="admin-hide-md">
                                        <span className="cx-badge cx-badge-neutral">{sourceLabel(lead.source)}</span>
                                    </td>
                                    <td className="admin-hide-lg">
                                        {lead.car_id && carLabel(lead) ? (
                                            <Link to={`/car/${lead.car_id}`} className="admin-link">
                                                {carLabel(lead)} <BoxArrowUpRight aria-hidden="true" />
                                            </Link>
                                        ) : "—"}
                                    </td>
                                    <td>
                                        <select
                                            className="cx-select admin-status-select"
                                            value={lead.status}
                                            aria-label={strings.change_status}
                                            disabled={saving}
                                            onChange={(e) => patchLead(lead.id, {status: e.target.value}, strings.status_updated)}
                                        >
                                            {STATUSES.map((value) => (
                                                <option key={value} value={value}>{statusLabel(value)}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="admin-hide-md admin-table-secondary">{formatDateTime(lead.created_at)}</td>
                                    <td>
                                        <div className="admin-row-actions">
                                            <button
                                                type="button"
                                                className="admin-icon-button"
                                                title={common.view}
                                                aria-label={`${common.view}: ${lead.name || lead.id}`}
                                                onClick={() => openLead(lead)}
                                            >
                                                <Eye aria-hidden="true" />
                                            </button>
                                            {canDelete && (
                                                <button
                                                    type="button"
                                                    className="admin-icon-button is-danger"
                                                    title={common.delete}
                                                    aria-label={`${common.delete}: ${lead.name || lead.id}`}
                                                    onClick={() => setDeleteTarget(lead)}
                                                >
                                                    <Trash3 aria-hidden="true" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <nav className="admin-pagination" aria-label={marketStrings.page_of}>
                        <Pagination
                            count={totalPages}
                            page={Math.min(page, totalPages)}
                            onChange={(event, value) => setPage(value)}
                            showFirstButton
                            showLastButton
                            color="primary"
                            variant="outlined"
                            shape="rounded"
                        />
                    </nav>
                )}
            </>
        );
    };

    const detailRow = (label, value) => {
        if (isBlank(value)) return null;
        return (
            <div className="admin-detail-row">
                <dt>{label}</dt>
                <dd>{display(value)}</dd>
            </div>
        );
    };

    return (
        <AdminLayout title={strings.title} subtitle={strings.subtitle}>
            <div className="admin-status-tabs" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={status === ""}
                    className={`admin-status-tab ${status === "" ? "is-active" : ""}`}
                    onClick={() => { setStatus(""); setPage(1); }}
                >
                    {common.all}
                </button>
                {STATUSES.map((value) => (
                    <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={status === value}
                        className={`admin-status-tab ${status === value ? "is-active" : ""}`}
                        onClick={() => { setStatus(value); setPage(1); }}
                    >
                        {statusLabel(value)}
                        {counts[value] > 0 && <span className="admin-status-count">{counts[value]}</span>}
                    </button>
                ))}
            </div>

            <div className="admin-toolbar admin-toolbar-wrap">
                <div className="admin-search">
                    <Search className="admin-search-icon" aria-hidden="true" />
                    <input
                        type="search"
                        className="cx-input admin-search-input"
                        placeholder={strings.search_placeholder}
                        aria-label={strings.search_placeholder}
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                    />
                </div>

                <select
                    className="cx-select admin-toolbar-select"
                    value={source}
                    aria-label={strings.filter_source}
                    onChange={(e) => { setSource(e.target.value); setPage(1); }}
                >
                    <option value="">{strings.filter_source}: {common.all}</option>
                    {SOURCES.map((value) => (
                        <option key={value} value={value}>{sourceLabel(value)}</option>
                    ))}
                </select>

                <label className="admin-date-filter">
                    <span>{strings.filter_date_from}</span>
                    <input type="date" className="cx-input" value={dateFrom}
                           onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
                </label>

                <label className="admin-date-filter">
                    <span>{strings.filter_date_to}</span>
                    <input type="date" className="cx-input" value={dateTo}
                           onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
                </label>

                {hasFilters && (
                    <button type="button" className="cx-btn cx-btn-ghost cx-btn-sm" onClick={clearFilters}>
                        <XLg aria-hidden="true" /> {marketStrings.clear_filters}
                    </button>
                )}

                <span className="admin-toolbar-count">{total}</span>
            </div>

            {body()}

            <Modal show={Boolean(active)} size="lg" centered onHide={() => setActive(null)} scrollable contentClassName="cx-dark-modal">
                {active && (
                    <>
                        <Modal.Header>
                            <Modal.Title>{strings.details_title} #{active.id}</Modal.Title>
                            <button type="button" className="admin-modal-close" aria-label={common.close} onClick={() => setActive(null)}>
                                <XLg aria-hidden="true" />
                            </button>
                        </Modal.Header>

                        <Modal.Body className="admin-modal-body">
                            <div className="admin-detail-head">
                                <span className={`cx-badge ${STATUS_TONE[active.status] || "cx-badge-neutral"}`}>
                                    {statusLabel(active.status)}
                                </span>
                                <span className="cx-badge cx-badge-neutral">{sourceLabel(active.source)}</span>
                            </div>

                            <dl className="admin-detail-grid">
                                {detailRow(strings.table_client, [active.name, active.surname].filter(Boolean).join(" "))}
                                {detailRow(strings.client_type, active.client_type)}
                                {detailRow(strings.est_amount, active.est_amount)}
                                {detailRow(strings.manufacturer, active.manufacturer)}
                                {detailRow(strings.subject, active.subject)}
                                {detailRow(strings.created_at, formatDateTime(active.created_at))}
                                {detailRow(strings.updated_at, formatDateTime(active.updated_at))}
                            </dl>

                            <div className="admin-detail-contacts">
                                {!isBlank(active.phone) && (
                                    <a className="cx-btn cx-btn-subtle" href={`tel:${String(active.phone).replace(/\s/g, "")}`}>
                                        <Telephone aria-hidden="true" /> {active.phone}
                                    </a>
                                )}
                                {!isBlank(active.email) && (
                                    <a className="cx-btn cx-btn-subtle" href={`mailto:${active.email}`}>
                                        <Envelope aria-hidden="true" /> {active.email}
                                    </a>
                                )}
                                {active.car_id && (
                                    <Link className="cx-btn cx-btn-subtle" to={`/car/${active.car_id}`}>
                                        <BoxArrowUpRight aria-hidden="true" /> {strings.open_car}
                                    </Link>
                                )}
                            </div>

                            {!isBlank(active.comment) && (
                                <div className="admin-detail-block">
                                    <h4>{strings.message}</h4>
                                    <p className="admin-detail-message">{active.comment}</p>
                                </div>
                            )}

                            <div className="admin-detail-block">
                                <h4>{strings.change_status}</h4>
                                <div className="admin-status-picker">
                                    {STATUSES.map((value) => (
                                        <button
                                            key={value}
                                            type="button"
                                            disabled={saving}
                                            className={`admin-status-option ${active.status === value ? "is-active" : ""}`}
                                            onClick={() => patchLead(active.id, {status: value}, strings.status_updated)}
                                        >
                                            {statusLabel(value)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="admin-detail-block">
                                <h4>{strings.admin_note}</h4>
                                <textarea
                                    className="cx-textarea"
                                    rows={4}
                                    placeholder={strings.admin_note_placeholder}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="cx-btn cx-btn-primary cx-btn-sm admin-note-save"
                                    disabled={saving || note === (active.admin_note || "")}
                                    onClick={() => patchLead(active.id, {admin_note: note}, strings.note_updated)}
                                >
                                    {saving
                                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        : strings.save_note}
                                </button>
                            </div>
                        </Modal.Body>

                        <Modal.Footer className="admin-modal-footer">
                            <div className="admin-modal-actions">
                                {canDelete && (
                                    <button type="button" className="cx-btn cx-btn-ghost" onClick={() => setDeleteTarget(active)}>
                                        <Trash3 aria-hidden="true" /> {common.delete}
                                    </button>
                                )}
                                <button type="button" className="cx-btn cx-btn-primary" onClick={() => setActive(null)}>
                                    {common.close}
                                </button>
                            </div>
                        </Modal.Footer>
                    </>
                )}
            </Modal>

            <ConfirmModal
                show={Boolean(deleteTarget)}
                title={strings.delete_title}
                message={strings.delete_message}
                detail={deleteTarget ? [deleteTarget.name, deleteTarget.surname].filter(Boolean).join(" ") : ""}
                confirmLabel={common.delete}
                loading={deleting}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </AdminLayout>
    );
};

export default AdminLeads;
